import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore, useEffect } from 'react';
import { api } from '@/auth/api';
import { fleetStore } from '@/lib/fleet-store';
import { isTenantAdminHost } from '@/config';
import { useAdminHostTenant } from './admin-host';
import { qk } from './query-keys';
import type {
  Fleet,
  FleetEntitlements,
  FleetMember,
  FleetTariffWithBands,
  VehicleType,
} from '@breaker/shared-types';
import type {
  FleetLogoPresignBody,
  UpdateFleetBody,
  UpdateFleetTariffBody,
} from '@breaker/shared-schemas';

export interface FleetMembershipSummary {
  fleet: Fleet;
  membership: FleetMember;
  /** FAD A3 (dispatcher+ rows): where this fleet's admin runs — its
   *  verified/live admin host, else the central dispatcher. The tenant-host
   *  fleet switcher redirects with this, never swaps tenants in place. */
  adminUrl?: string;
}

/**
 * What to assume before we know which fleet we're on. Everything off: nav
 * should appear as entitlements resolve, never flash on and then vanish, and
 * "no fleet" must not imply "all surfaces".
 */
const NO_ENTITLEMENTS: FleetEntitlements = {
  bays: false,
  social: false,
  tms: false,
  customerPortal: false,
};

export function useFleets(enabled = true) {
  return useQuery({
    queryKey: qk.me.fleets(),
    enabled,
    queryFn: () => api<{ fleets: FleetMembershipSummary[] }>('/v1/me/fleets'),
    select: (data) => data.fleets,
  });
}

/**
 * The primary hook for "what fleet are we operating on right now". Combines
 * the user's fleet list with the active-fleet store, with sensible fallbacks:
 *
 *   - No fleets: returns null (caller should show an empty-state)
 *   - Active id stored but not in user's fleets (membership revoked): falls
 *     back to the first fleet and updates the store
 *   - No active id stored: defaults to the first fleet and persists it
 */
export function useActiveFleet(): {
  fleet: Fleet | null;
  /** The signed-in user's role in the active fleet. */
  role: FleetMembershipSummary['membership']['role'] | null;
  /**
   * Which surfaces the active fleet has (0037), for per-fleet nav gating.
   * Already narrowed server-side by the global FEATURE_TMS_ENABLED flag, so
   * this reads as "what actually works" — gate on it directly, no second
   * check needed. Falls back to all-off while loading or with no fleet.
   */
  entitlements: FleetEntitlements;
  fleets: FleetMembershipSummary[];
  isLoading: boolean;
  error: unknown;
  setActive(fleetId: string): void;
} {
  const fleetsQuery = useFleets();
  // FAD A3: on a tenant admin host the Host pins the fleet — the stored
  // choice never overrides it (and the api's hostFleetGuard would 403 any
  // cross-fleet call anyway). Query is disabled everywhere else.
  const adminTenant = useAdminHostTenant();
  const stored = useSyncExternalStore(
    (cb) => fleetStore.subscribe(cb),
    () => fleetStore.get(),
    () => fleetStore.get(),
  );

  const fleets = fleetsQuery.data ?? [];
  const fleetIds = new Set<string>(fleets.map((f) => f.fleet.id));

  const resolved = (() => {
    if (fleets.length === 0) return null;
    if (isTenantAdminHost()) {
      // Membership missing on this host → null (signed in, but not a member
      // of the fleet this address serves); the empty state steers to central.
      const hostFleetId = adminTenant.data?.fleetId ?? null;
      return hostFleetId ? (fleets.find((f) => f.fleet.id === hostFleetId) ?? null) : null;
    }
    if (stored && fleetIds.has(stored)) {
      return fleets.find((f) => f.fleet.id === stored) ?? null;
    }
    return fleets[0]!;
  })();

  // Reconcile: if we landed on a different fleet than what's stored, persist
  // the choice so reloads stick. Effect, not during render.
  useEffect(() => {
    if (!resolved) return;
    if (stored !== resolved.fleet.id) {
      fleetStore.set(resolved.fleet.id);
    }
  }, [resolved, stored]);

  return {
    fleet: resolved?.fleet ?? null,
    role: resolved?.membership.role ?? null,
    entitlements: resolved?.fleet.entitlements ?? NO_ENTITLEMENTS,
    fleets,
    isLoading:
      fleetsQuery.isLoading || (isTenantAdminHost() && adminTenant.isLoading),
    error: fleetsQuery.error,
    setActive: (id: string) => fleetStore.set(id),
  };
}

/**
 * PATCH /v1/fleets/:fleetId — owner-gated server-side. First writable fleet
 * setting (vatRegistered); future settings (geofence radius, branding) join
 * the same body. Invalidates the fleets list so useActiveFleet refreshes.
 */
export function useUpdateFleet(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFleetBody) =>
      api<{ fleet: Fleet }>(`/v1/fleets/${fleetId}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me.fleets() });
    },
  });
}

/**
 * PUT /v1/fleets/:fleetId/portal-slug — set/clear the portal subdomain
 * (U8/D5). Server-side it moves fleets.portal_slug and the materialised
 * wildcard host together; 409 when another fleet holds the label.
 */
export function useSetPortalSlug(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (portalSlug: string | null) =>
      api<{ fleet: Fleet }>(`/v1/fleets/${fleetId}/portal-slug`, {
        method: 'PUT',
        body: { portalSlug },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me.fleets() });
    },
  });
}

export interface FleetMailIdentity {
  from: string;
  replyTo: string | null;
  configured: boolean;
}

/** Read-only: which From/Reply-To the fleet's portal mail goes out under. */
export function useMailIdentity(fleetId: string | null) {
  return useQuery({
    queryKey: ['fleets', fleetId ?? '', 'mail-identity'] as const,
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ identity: FleetMailIdentity }>(`/v1/fleets/${fleetId}/mail-identity`),
    select: (data) => data.identity,
    staleTime: 5 * 60_000,
  });
}

interface LogoPresignResult {
  uploadUrl: string;
  r2Key: string;
  publicUrl: string;
  uploadHeaders: Record<string, string>;
  expiresAt: string;
}

/**
 * Full logo flow (U5/U8): presign → PUT the bytes straight to R2 → PATCH the
 * public URL into fleets.logoUrl. Resolves to the stored public URL.
 */
export function useUploadLogo(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const body: FleetLogoPresignBody = {
        mimeType: file.type as FleetLogoPresignBody['mimeType'],
        sizeBytes: file.size,
      };
      const presigned = await api<LogoPresignResult>(
        `/v1/fleets/${fleetId}/logo-upload`,
        { method: 'POST', body },
      );
      const put = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: presigned.uploadHeaders,
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      await api<{ fleet: Fleet }>(`/v1/fleets/${fleetId}`, {
        method: 'PATCH',
        body: { logoUrl: presigned.publicUrl },
      });
      return presigned.publicUrl;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me.fleets() });
    },
  });
}

// ============================================================
// Rate card (0046 / U9)
// ============================================================

export interface TariffDefaults {
  vatRatePercent: number;
  oohPercent: number;
  oohStartHour: number;
  oohEndHour: number;
  oohWeekends: boolean;
  oohBankHolidays: boolean;
  roadFactor: number;
  extraStopFeePence: number;
  waitingRatePence: number;
  waitingFreeMinutes: number;
  surcharges: { label: string; amountPence: number }[];
  bands: {
    sort: number;
    label: string;
    vehicleTypes: VehicleType[];
    ratePerMilePence: number | null;
    minimumPence: number | null;
    pricedOnConfirmation: boolean;
  }[];
}

/** GET /v1/fleets/:fleetId/tariff — stored card (null = pricing on defaults). */
export function useTariff(fleetId: string | null) {
  return useQuery({
    queryKey: ['fleets', fleetId ?? '', 'tariff'] as const,
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ tariff: FleetTariffWithBands | null; defaults: TariffDefaults }>(
        `/v1/fleets/${fleetId}/tariff`,
      ),
    staleTime: 30_000,
  });
}

/** PUT /v1/fleets/:fleetId/tariff — replace the whole card (owner). */
export function useUpdateTariff(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFleetTariffBody) =>
      api<{ tariff: FleetTariffWithBands }>(`/v1/fleets/${fleetId}/tariff`, {
        method: 'PUT',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fleets', fleetId ?? '', 'tariff'] });
    },
  });
}
